import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../config/apiConfig";

/**
 * Runtime branding.
 *
 * Nothing about the product's identity is compiled into this bundle. The name,
 * tagline, logo, colours and locale all come from App_Settings.
 *
 * Which values arrive depends on who is asking, and there are two answers:
 *
 *   Not signed in  GET /api/Settings/public - anonymous, so the login screen can
 *                  render branded. It serves the platform values, because one
 *                  deployment answers on one URL and a visitor at the sign-in
 *                  page has no organisation yet.
 *
 *   Signed in      GET /api/Settings/client - the caller's own organisation,
 *                  falling back to the platform default for anything that
 *                  organisation has not set. This is what re-brands the app the
 *                  moment somebody logs in.
 *
 * The same build can therefore be handed to any client, and every organisation
 * inside it can look like itself.
 */

const FALLBACK = {
  "app.name": "ERP",
  "app.short_name": "ERP",
  "app.tagline": "Enterprise Resource Planning",
  "app.legal_name": "",
  "app.logo_url": "",
  "app.login_logo_url": "",
  "app.favicon_url": "",
  "app.primary_color": "#0f172a",
  "app.accent_color": "#0284c7",
  "app.sidebar_text": "#cbd5e1",
  "app.footer_text": "",
  "app.support_email": "",
  "locale.currency_symbol": "Rs.",
  "locale.currency_code": "INR",
  "locale.date_format": "dd-MM-yyyy",
  "security.idle_timeout_ms": "2000000",
  "security.captcha_enabled": "true",
};

const CACHE_KEY = "erp_branding_cache";

const BrandingContext = createContext(null);

/** Reads the last known branding so a reload paints instantly, before the fetch. */
const readCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? { ...FALLBACK, ...JSON.parse(raw) } : FALLBACK;
  } catch {
    return FALLBACK;
  }
};

export const BrandingProvider = ({ children }) => {
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userId = useSelector((state) => state.auth.user?.userId);

  const [settings, setSettings] = useState(readCache);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      // Signed in, so ask for this user's organisation. If that call fails - an
      // expired session, say - fall back to the anonymous endpoint rather than
      // leaving the app unbranded.
      const endpoint = isLoggedIn
        ? `${API_BASE_URL}/api/Settings/client`
        : `${API_BASE_URL}/api/Settings/public`;

      let data;
      try {
        ({ data } = await axios.get(endpoint));
      } catch (err) {
        if (!isLoggedIn) throw err;
        ({ data } = await axios.get(`${API_BASE_URL}/api/Settings/public`));
      }

      const incoming = data?.settings || data?.Settings || {};

      // Blank values in the DB should fall back rather than render as empty.
      const merged = { ...FALLBACK };
      Object.keys(incoming).forEach((key) => {
        const value = incoming[key];
        if (value !== null && value !== undefined && `${value}`.trim() !== "") {
          merged[key] = value;
        }
      });

      setSettings(merged);
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(merged));
    } catch {
      // Offline or API down: keep whatever we already have. The app must still
      // be usable enough to sign in and fix the configuration.
      setSettings(readCache());
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  // Re-resolve on sign-in and sign-out, and when a different person signs in on
  // the same tab: their organisation's branding is not the previous one's.
  useEffect(() => {
    load();
  }, [load, userId]);

  const get = useCallback(
    (key, fallback = "") => {
      const value = settings[key];
      return value === undefined || value === null || `${value}`.trim() === ""
        ? fallback
        : value;
    },
    [settings]
  );

  const appName = get("app.name", "ERP");

  // Push the identity into the document and into CSS variables so plain CSS can
  // theme against the client's colours too.
  useEffect(() => {
    document.title = appName;

    const root = document.documentElement;
    root.style.setProperty("--erp-primary", get("app.primary_color", "#0f172a"));
    root.style.setProperty("--erp-accent", get("app.accent_color", "#0284c7"));
    root.style.setProperty("--erp-sidebar-text", get("app.sidebar_text", "#cbd5e1"));

    const favicon = get("app.favicon_url");
    if (favicon) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = favicon;
    }
  }, [appName, get]);

  const value = useMemo(
    () => ({
      settings,
      loading,
      get,
      reload: load,

      appName,
      shortName: get("app.short_name", appName.slice(0, 3).toUpperCase()),
      tagline: get("app.tagline"),
      legalName: get("app.legal_name", appName),
      logoUrl: get("app.logo_url"),
      loginLogoUrl: get("app.login_logo_url") || get("app.logo_url"),
      footerText: get("app.footer_text"),
      supportEmail: get("app.support_email"),

      primaryColor: get("app.primary_color", "#0f172a"),
      accentColor: get("app.accent_color", "#0284c7"),
      sidebarText: get("app.sidebar_text", "#cbd5e1"),

      currencySymbol: get("locale.currency_symbol", "Rs."),
      currencyCode: get("locale.currency_code", "INR"),
      dateFormat: get("locale.date_format", "dd-MM-yyyy"),

      idleTimeoutMs: parseInt(get("security.idle_timeout_ms", "2000000"), 10),
      captchaEnabled: `${get("security.captcha_enabled", "true")}` === "true",

      /** Initials shown when no logo has been uploaded. */
      initials: appName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0])
        .join("")
        .toUpperCase(),
    }),
    [settings, loading, get, load, appName]
  );

  return (
    <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    throw new Error("useBranding must be used inside <BrandingProvider>");
  }
  return ctx;
};

export default BrandingContext;
